/* eslint-disable camelcase */
import { Box, Card, ChevronRightIcon, NavList } from '@deckee/deck-hand';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { useTheme, type DefaultTheme } from 'styled-components';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import HeaderBar from '../../../layouts/components/HeaderBar/HeaderBar';
import HeaderBarButton from '../../../layouts/components/HeaderBarButton/HeaderBarButton';
import { formatLatLon } from '../../../helpers/coordinate-helpers';
import IconWithTextLockup from '../../../lockups/IconWithTextLockup/IconWithTextLockup';
import BlueCircleWrapper from '../../../shared/BlueCircleWrapper/BlueCircleWrapper';
import {
  ReactSvg,
  rules,
  lifeJacket,
  environmentalZone,
  restrictedZone,
  speedZone,
  activityZone,
  location,
  back,
} from '../../../shared/vector-images';
import { callEndpoint, telemetry } from '../../../../services/backend';
import {
  transitionToNextRoute,
  transitionToPreviousRoute,
} from '../../../../services/route/route-navigation';
import {
  selectUnitsConfig,
  showSelectMapPoint,
} from '../../../../services/store/modules/app-controls';
import {
  ROUTE_DROP_PIN,
  ROUTE_MARKER_SUMMARY_DETAILS,
  ROUTE_PREPARE,
} from '../../../../services/route/route-names';
import PartnersCard from '../../Prepare/components/PartnersCard/PartnersCard';

const space = (theme: DefaultTheme, key: string): number => {
  const scale = theme.space as unknown as Record<string, number>;
  return scale[key] ?? 0;
};

/** Sticky header elevation — theme colours + space scale only (no raw rgba). */
const stickyHeaderShadow = (theme: DefaultTheme) =>
  `0 1px ${space(theme, '2')}px color-mix(in srgb, ${theme.colors.black} 7%, transparent), 0 0 ${space(theme, '1')}px color-mix(in srgb, ${theme.colors.black} 7%, transparent)`;

const StickyHeaderSection = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 1;
  box-shadow: ${({ theme }) => stickyHeaderShadow(theme)};
`;

const LocationSelectorRow = styled(Box).attrs({
  as: 'button',
  type: 'button',
})`
  appearance: none;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.lightergrey};
  border-radius: ${({ theme }) => space(theme, '2.5')}px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;

  @media (hover: hover) {
    &:hover {
      background: ${({ theme }) => theme.colors.lightgrey};
    }
  }
`;

const getZoneConfig = (
  title: string | null | undefined,
  theme: DefaultTheme,
) => {
  if (!title) {
    return {
      icon: rules,
      bg: theme.colors.secondarylight,
      fill: theme.colors.secondary,
    };
  }
  const lower = title.toLowerCase();
  if (lower.startsWith('environmental')) {
    return {
      icon: environmentalZone,
      bg: theme.colors.warninglight,
      fill: theme.colors.warning,
    };
  }
  if (lower.startsWith('restricted')) {
    return {
      icon: restrictedZone,
      bg: theme.colors.dangerlight,
      fill: theme.colors.danger,
    };
  }
  if (lower.startsWith('speed')) {
    return {
      icon: speedZone,
      bg: theme.colors.secondarylight,
      fill: theme.colors.secondary,
    };
  }
  if (lower.startsWith('activity')) {
    return {
      icon: activityZone,
      bg: theme.colors.successlight,
      fill: theme.colors.success,
    };
  }
  return {
    icon: rules,
    bg: theme.colors.secondarylight,
    fill: theme.colors.secondary,
  };
};

const handleSelectRegulation = ({ url }: { url: string }) => {
  transitionToNextRoute(ROUTE_MARKER_SUMMARY_DETAILS, {
    url: encodeURIComponent(url),
  });
};

const RegulationListPage: React.FC<{
  latitude: number;
  longitude: number;
}> = ({ latitude, longitude }) => {
  const theme = useTheme();
  const unitsConfig = useSelector(selectUnitsConfig);
  const [regulations, setRegulations] = useState<
    {
      regulation_card: {
        title: string;
        description: string;
      };
      details_available: boolean;
      url: string;
    }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const updateRegulations = async () => {
      setIsLoading(true);
      try {
        const data = await callEndpoint(
          callEndpoint.endpoints.getRegulationsByLocation,
          {
            ':latitude': latitude,
            ':longitude': longitude,
          },
        );
        setRegulations(data);
      } catch (err) {
        setRegulations([]);
        const message = err instanceof Error ? err.message : String(err);
        telemetry(
          telemetry.WARN,
          `[RegulationListPage] Failed to load regulations: ${message}`,
        );
      } finally {
        setIsLoading(false);
      }
    };
    dispatch(showSelectMapPoint(-1));
    updateRegulations();
  }, [latitude, longitude, dispatch]);

  return (
    <BasicDrawerLayout
      loading={isLoading}
      renderHeaderBar={
        <StickyHeaderSection p={0} m={0} bg="white">
          <HeaderBar
            solidBackground
            centerNode="Regulations"
            leftNode={
              <HeaderBarButton
                ariaLabel="return"
                icon={back()}
                onClick={() => transitionToPreviousRoute()}
              />
            }
          />
          <Box px={3} pt={0} pb={2}>
            <LocationSelectorRow
              display="flex"
              alignItems="center"
              px={3}
              py={2.5}
              bg="white"
              aria-label="Change dropped pin location"
              onClick={() => transitionToNextRoute(ROUTE_DROP_PIN)}
            >
              <IconWithTextLockup
                compact
                useEllipsis
                flex="1 1 auto"
                image={
                  <BlueCircleWrapper iconSize={20}>
                    <ReactSvg
                      toDisplay={location()}
                      options={{ fillColor: theme.colors.secondary }}
                      width={20}
                      height={20}
                    />
                  </BlueCircleWrapper>
                }
                heading={
                  <Box fontSize={2} fontWeight={600}>
                    Dropped pin
                  </Box>
                }
                description={
                  <Box fontSize={2} color="darkergrey">
                    {formatLatLon(unitsConfig?.coordinate, latitude, longitude)}
                  </Box>
                }
              />
              <Box flex="0 0 auto" ml={2} display="flex" alignItems="center">
                <ChevronRightIcon color={theme.colors.primary} />
              </Box>
            </LocationSelectorRow>
          </Box>
        </StickyHeaderSection>
      }
    >
      <Box display="flex" flexDirection="column" gap={2} p={2}>
        {!(regulations.length > 0) && (
          <Card>
            <Card.Heading fontWeight="bold" color="black">
              Applicable zones
            </Card.Heading>
            <Card.Body>
              <NavList edge>
                <NavList.Item>
                  <IconWithTextLockup
                    flex="1 1 0px"
                    useEllipsis
                    image={
                      <BlueCircleWrapper bg={theme.colors.lightergrey}>
                        <ReactSvg
                          toDisplay={rules()}
                          options={{
                            fillColor: theme.colors.black,
                          }}
                          width={24}
                          height={24}
                        />
                      </BlueCircleWrapper>
                    }
                    heading="No known regulations in this area"
                  />
                </NavList.Item>
              </NavList>
            </Card.Body>
          </Card>
        )}
        {regulations.length > 0 && (
          <Card>
            <Card.Heading fontWeight="bold" color="black">
              {regulations.length} Applicable zone
              {regulations.length !== 1 ? 's' : ''}
            </Card.Heading>
            <Card.Body>
              <NavList edge>
                {regulations.map(
                  ({
                    regulation_card: { title, description },
                    url,
                    details_available,
                  }) => {
                    const { icon, bg, fill } = getZoneConfig(title, theme);
                    return (
                      <NavList.Item
                        key={url}
                        chevron={details_available}
                        onClick={
                          details_available
                            ? () => handleSelectRegulation({ url })
                            : undefined
                        }
                      >
                        <IconWithTextLockup
                          flex="1 1 0px"
                          useEllipsis
                          emphasis="heading"
                          image={
                            <BlueCircleWrapper bg={bg}>
                              <ReactSvg
                                toDisplay={icon()}
                                options={{
                                  fillColor: fill,
                                }}
                                width={24}
                                height={24}
                              />
                            </BlueCircleWrapper>
                          }
                          heading={title ?? 'Regulation'}
                          description={description}
                        />
                      </NavList.Item>
                    );
                  },
                )}
              </NavList>
            </Card.Body>
          </Card>
        )}
        <Card>
          <Card.Heading fontWeight="bold" color="black">
            Mandatory gear
          </Card.Heading>
          <Card.Body>
            <NavList edge>
              <NavList.Item
                chevron
                onClick={() => transitionToNextRoute(ROUTE_PREPARE)}
              >
                <IconWithTextLockup
                  flex="1 1 0px"
                  useEllipsis
                  image={
                    <BlueCircleWrapper>
                      <ReactSvg
                        toDisplay={lifeJacket()}
                        options={{
                          fillColor: theme.colors.secondary,
                        }}
                        width={24}
                        height={24}
                      />
                    </BlueCircleWrapper>
                  }
                  heading="View personalized checklist"
                />
              </NavList.Item>
            </NavList>
          </Card.Body>
        </Card>
        <PartnersCard />
      </Box>
    </BasicDrawerLayout>
  );
};

export default RegulationListPage;
